import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Headphones } from 'lucide-react';
import { useAudio } from '../../context/AudioContext';
import { mediaUrl } from '../../lib/programsApi';

const SERIF = { fontFamily: "'Playfair Display', serif" };

// Full-length session video. Audio stays the default: opening pauses the global player at its position,
// the video picks up there, and closing hands the position back so the session never restarts.
export const SessionVideoModal = ({ open, content, onClose, onEnded, eyebrow = 'Watch the session', closeLabel, note }) => {
  const { state, pause, resume, seek, play } = useAudio();
  const videoRef = useRef(null);
  const startAt = useRef(0);
  const isCurrent = content && state.current?.contentId === content.id;

  useEffect(() => {
    if (!open) return undefined;
    startAt.current = isCurrent ? state.position || 0 : 0;
    if (isCurrent && state.playing) pause();
    const v = videoRef.current;
    const onMeta = () => { if (v && startAt.current > 1) v.currentTime = startAt.current; };
    v?.addEventListener('loadedmetadata', onMeta);
    return () => v?.removeEventListener('loadedmetadata', onMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !content?.videoUrl) return null;

  const backToAudio = () => {
    const t = videoRef.current?.currentTime || 0;
    videoRef.current?.pause();
    if (isCurrent) { seek(t); resume(); }
    else if (content.audioUrl) {
      play({ src: mediaUrl(content.audioUrl), title: content.title, subtitle: content.creator || 'Rowdy Solomon', artwork: mediaUrl(content.coverImageUrl, 800), contentId: content.id, sourceContext: 'program', kind: 'audio' });
      if (t > 1) setTimeout(() => seek(t), 900);
    }
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-[#0f1e33]/95 backdrop-blur-sm flex flex-col" data-testid="session-video-modal">
      <div className="flex items-center justify-between px-4 pt-[max(env(safe-area-inset-top),12px)] pb-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#e3d2a8]/80">{eyebrow}</p>
          <h3 className="text-base text-[#f9f4ea] truncate" style={SERIF} data-testid="session-video-title">{content.title}</h3>
        </div>
        <button type="button" onClick={backToAudio} className="w-9 h-9 rounded-full bg-white/10 text-white inline-flex items-center justify-center" aria-label="Close video" data-testid="session-video-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center px-2">
        <video
          ref={videoRef}
          src={mediaUrl(content.videoUrl)}
          poster={mediaUrl(content.coverImageUrl, 1200) || undefined}
          controls
          autoPlay
          playsInline
          preload="metadata"
          onEnded={() => onEnded?.()}
          className="w-full max-h-[70vh] rounded-2xl bg-black shadow-2xl"
          data-testid="session-video-player"
        />
      </div>
      <div className="px-5 pb-[max(env(safe-area-inset-bottom),20px)] pt-3">
        <button type="button" onClick={backToAudio} className="w-full h-12 rounded-full bg-[#D6AD60] text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 hover:bg-[#c9a866] transition-colors" data-testid="session-video-back-to-audio">
          <Headphones className="w-4 h-4" /> {closeLabel || 'Continue with audio'}
        </button>
        <p className="text-center text-[11px] text-[#e3d2a8]/70 mt-2">{note || 'Audio keeps playing with your screen locked. Video picks up where the audio left off.'}</p>
      </div>
    </div>, document.body);
};
